import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import { Injectable } from '@angular/core';
import { environment } from '@env';
import { ToastService } from './toast.service';
import { Subject } from 'rxjs';  // Import RxJS Subject
import { NbComponentStatus } from '@nebular/theme';
import { NotificationSoundService } from './notification-sound.service';

@Injectable({
  providedIn: 'root',
})
export class FCMService {
  message = null;
  private fcmToken: string = '';
  private notificationsEnabled = true;
  // Create a Subject to emit new notification events
  // eslint-disable-next-line
  private notificationSubject = new Subject<any>();

  constructor(
    private toastService: ToastService,
    private notificationSoundService: NotificationSoundService
  ) {
    void this.listenForMessages(); // Start listening for notifications when service is initialized
  }

  // Expose the notification subject as an observable so that other components can subscribe
  getNotificationObservable() {
    return this.notificationSubject.asObservable();
  }

  async requestPermission(): Promise<string | null> {
    if (!(await this.isMessagingAvailable())) {
      console.warn('Firebase messaging is not supported in this browser/environment.');
      return null;
    }

    if (this.fcmToken) {
      return this.fcmToken;
    }

    if (typeof Notification !== 'undefined') {
      if (Notification.permission === 'denied') {
        return null;
      }

      if (Notification.permission !== 'granted') {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          return null;
        }
      }
    }

    return this.retrieveMessagingToken();
  }

  private async retrieveMessagingToken(): Promise<string | null> {
    const messaging = getMessaging();

    try {
      const serviceWorkerRegistration = await this.registerMessagingServiceWorker();
      const currentToken = await getToken(messaging, {
        vapidKey: environment.firebase.vapidKey,
        serviceWorkerRegistration: serviceWorkerRegistration || undefined,
      });

      if (currentToken) {
        this.fcmToken = currentToken;
        localStorage.setItem('fcm_token', currentToken);
      } else {
        console.log('No registration token available.');
      }
    } catch (err) {
      console.error('An error occurred while retrieving token:', err);
    }

    return this.fcmToken || null;
  }

  getFcmToken(): string {
    if (!this.fcmToken) {
      this.fcmToken = localStorage.getItem('fcm_token') ?? '';
    }

    return this.fcmToken;
  }

  // Method to listen for foreground messages
  async listenForMessages(): Promise<void> {
    if (!(await this.isMessagingAvailable())) {
      return;
    }

    const messaging = getMessaging();
    onMessage(messaging, (payload) => {
      const notificationTitle = payload?.notification?.title || payload?.data?.['title'] || 'New notification';
      const notificationBody = payload?.notification?.body || payload?.data?.['body'] || '';
      if (this.notificationsEnabled) {
        this.showNotification(notificationTitle, notificationBody, 'info');
      }
      // Emit the new notification event
      this.notificationSubject.next(payload);
      this.message = payload;
    });
  }

  showNotification(title: string, message: string, type: NbComponentStatus) {
    this.playNotificationSound();
    this.toastService.showToast(type, title, message, 5000);
  }

  toggleNotifications(status: boolean): void {
    this.notificationsEnabled = status;
  }

  playNotificationSound() {
    this.notificationSoundService.play();
  }

  private async isMessagingAvailable(): Promise<boolean> {
    if (typeof window === 'undefined') {
      return false;
    }

    if (!('serviceWorker' in navigator) || !('Notification' in window)) {
      return false;
    }

    try {
      return await isSupported();
    } catch {
      return false;
    }
  }

  private async registerMessagingServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    if (!('serviceWorker' in navigator)) {
      return null;
    }

    const scope = '/firebase-cloud-messaging-push-scope';
    const existingRegistration = await navigator.serviceWorker.getRegistration(scope);
    if (existingRegistration) {
      return existingRegistration;
    }

    return navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope });
  }
}
